import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../utils/api';
import { API_CONFIG } from '../utils/apiConfig';
import { showToast } from '../utils/helpers';
import sanitizeString from '../utils/sanitizeLabel';
import { 
  loadCart, 
  saveCart, 
  setupCartSync, 
  clearCart as clearCartStorage 
} from '../utils/cart';
import StoreFrontFooter from '../components/store/footer';
import ContactStoreModal from '../components/modals/ContactStoreModal';
import CartModal from '../components/modals/CartModal';
import Header from '../components/store/header';
import ShareStoreModal from '../components/modals/ShareProductModal';
import { useAuth } from '../contexts/AuthContext';
import { useMeoContext } from '../contexts/MeoContext';
import { ShoppingBag, Search, Filter, ChevronDown, Star, Check } from 'lucide-react';

// Constants
const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'popular', label: 'Popular' },
  { value: 'price-low', label: 'Price: Low to High' },
  { value: 'price-high', label: 'Price: High to Low' },
  { value: 'name', label: 'Alphabetical' }
];

const PRODUCTS_PER_PAGE = 24;

const StorePage = ({username}) => {
  const { companyId: urlCompanyId } = useParams();
  const companyId = urlCompanyId || username;
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('grid');
  const [priceRange, setPriceRange] = useState([0, 100000]);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showCartModal, setShowCartModal] = useState(false);
  const [cart, setCart] = useState([]);
  const [showShareStoreModal, setShowShareStoreModal] = useState(false);
  const [selectedVariations, setSelectedVariations] = useState({});
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);
  const [failedImages, setFailedImages] = useState(() => new Set());
  const [storeLogoFailed, setStoreLogoFailed] = useState(false);
  const { currentUser } = useAuth();
  const { setShowMeo } = useMeoContext();
  const baseUrl = window.location.origin;
  const shareLink = `https://${companyId}.${window.location.hostname}`

  // Pagination states
  const [allProducts, setAllProducts] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalProducts: 0,
    hasNext: false,
    hasPrev: false,
    limit: PRODUCTS_PER_PAGE
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [allProductsLoaded, setAllProductsLoaded] = useState(false);

  const cartInitializedRef = useRef(false);

  // Memoized values
  const cartItemCount = useMemo(() =>
    cart.reduce((count, item) => count + item.quantity, 0),
    [cart]
  );

  const cartTotal = useMemo(() =>
    cart.reduce((total, item) => total + ((item.finalPrice || item.per_unit_cost) * item.quantity), 0),
    [cart]
  );

  // Helper function to get product image
  const getProductImage = useCallback((product) => {
    if (!product) return null;

    if (product.images && Array.isArray(product.images) && product.images.length > 0) {
      return product.images[0].image_url || product.images[0].url || product.images[0];
    }

    if (product.product_image) {
      const images = product.product_image.split(',');
      return images[0].trim();
    }

    return null;
  }, []);

  // Graceful image failure handling: once a product image fails to load (e.g.
  // an expired Cloudinary authenticated URL returning 401), remember it and
  // stop retrying — render a placeholder instead of re-requesting the URL.
  const isProductImageBroken = useCallback((productKey) => {
    return failedImages.has(productKey);
  }, [failedImages]);

  const markProductImageBroken = useCallback((productKey) => {
    if (failedImages.has(productKey)) return;
    setFailedImages(prev => new Set(prev).add(productKey));
  }, [failedImages]);

  const handleStoreLogoError = useCallback(() => {
    setStoreLogoFailed(true);
  }, []);

  const min = 0;
  const max = priceRange[1];
  const percent = Math.round(((priceRange[0] - min) / (max - min || 1)) * 100);

  // Enhanced cart synchronization (per-store)
  useEffect(() => {
    // Setup cross-tab synchronization
    const cleanup = setupCartSync(companyId, () => {
      // Reload cart when updated in another tab
      loadCart(companyId).then(storeCart => setCart(storeCart));
    });

    return cleanup;
  }, [companyId]);

  // Enhanced cart loading with better error handling
  useEffect(() => {
    const initializeCart = async () => {
      try {
        const storeCart = await loadCart(companyId);
        console.log(' StorePage: Initial cart load:', storeCart.length, 'items');
        cartInitializedRef.current = true;
        setCart(storeCart);
      } catch (error) {
        console.error('StorePage: Error initializing cart:', error);
        cartInitializedRef.current = true;
        setCart([]);
      }
    };

    initializeCart();
  }, [companyId]);

  // Enhanced cart saving with debouncing
  useEffect(() => {
    if (!cartInitializedRef.current) return;

    const saveCartData = async () => {
      try {
        await saveCart(cart, companyId);
        console.log(' StorePage: Cart saved successfully');
      } catch (error) {
        console.error(' StorePage: Error saving cart:', error);
      }
    };

    // Debounce cart saves to prevent excessive writes
    const timeoutId = setTimeout(saveCartData, 500);
    return () => clearTimeout(timeoutId);
  }, [cart, companyId]);

  // Helper functions
  const handleCategorySelect = useCallback((category) => {
    setSelectedCategory(category);
    setShowCategoriesModal(false);
  }, []);

  const handlePriceRangeApply = useCallback(() => {
    setShowCategoriesModal(false);
  }, []);

  const getConditionLabel = useCallback((condition) => {
    const conditions = {
      'new': 'New',
      'used': 'Used',
      'refurbished': 'Refurbished'
    };
    return conditions[condition] || condition || 'Not specified';
  }, []);

  const getDeliveryTimelineLabel = useCallback((timeline) => {
    const timelines = {
      '1': '1 day',
      '2': '2 days',
      '3': '3 days',
      '7': '1 week',
      '14': '14 days',
      '21': '21 days'
    };
    return timelines[timeline] || (timeline ? `${timeline} days` : 'Not specified');
  }, []);

  // Enhanced addToCart with better persistence
  const addToCart = useCallback((product, variation = null) => {
    const isDigital = product.is_digital === true || product.is_digital === 'true' || product.is_digital === 1;
    
    const productImages = getProductImage(product) ? [getProductImage(product)] : [];
    const primaryImage = productImages.length > 0 ? productImages[0] : null;

    const cartItem = {
      ...product,
      quantity: 1,
      variation: variation || null,
      finalPrice: variation ? (product.per_unit_cost + (variation.price_adjustment || 0)) : product.per_unit_cost,
      product_image: primaryImage,
      images: productImages,
      is_digital: isDigital,
      addedAt: new Date().toISOString()
    };

    setCart(prevCart => {
      const existingItemIndex = prevCart.findIndex(item =>
        item.id === product.id &&
        JSON.stringify(item.variation) === JSON.stringify(variation)
      );

      let updatedCart;
      
      if (existingItemIndex > -1) {
        // For digital products, don't increase quantity beyond 1
        if (isDigital) {
          showToast('Digital product already in cart', 'info');
          return prevCart;
        }
        
        updatedCart = [...prevCart];
        updatedCart[existingItemIndex] = {
          ...updatedCart[existingItemIndex],
          quantity: updatedCart[existingItemIndex].quantity + 1,
          updatedAt: new Date().toISOString()
        };
      } else {
        updatedCart = [...prevCart, cartItem];
      }

      return updatedCart;
    });

    showToast('Added to cart!', 'success');
  }, [getProductImage, companyId]);

  // Enhanced removeFromCart with immediate persistence
  const removeFromCart = useCallback((productId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== productId));
    showToast('Product removed from cart', 'info');
  }, []);

  // Enhanced updateQuantity with persistence
  const updateQuantity = useCallback((productId, newQuantity) => {
    setCart(prevCart =>
      prevCart
        .map(item => {
          if (item.id !== productId) return item;
          const isDigital = item.is_digital === true || item.is_digital === 'true' || item.is_digital === 1;

          if (isDigital && newQuantity !== 1) {
            showToast('Digital products quantity cannot be changed', 'info');
            return item;
          }

          if (newQuantity < 1) {
            return { ...item, quantity: 0 };
          }

          return {
            ...item,
            quantity: newQuantity,
            updatedAt: new Date().toISOString()
          };
        })
        .filter(item => item.quantity > 0)
    );
  }, []);

  // Enhanced clearCart with persistence
  const clearCartLocal = useCallback(() => {
    setCart([]);
    clearCartStorage(companyId);
    showToast('Cart cleared', 'info');
  }, [companyId]);

  const handleVariationSelect = useCallback((productId, variation) => {
    setSelectedVariations(prev => ({
      ...prev,
      [productId]: variation
    }));
  }, []);

  const getAvailableStock = useCallback((product, variation = null) => {
    if (variation) {
      return variation.inventory || 0;
    }
    return (product.inventory || 0) - (product.total_sold || 0);
  }, []);

  const formatCurrency = useCallback((amount, currency = 'NGN') => {
    if (!amount) return '₦0';
    try {
      const options = {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      };
      const locale = currency === 'NGN' ? 'en-NG' : 'en-US';
      return new Intl.NumberFormat(locale, options).format(amount);
    } catch (error) {
      return currency === 'NGN' ? `₦${amount}` : `$${amount}`;
    }
  }, []);

  // Helper to check if product is digital
  const isDigitalProduct = useCallback((product) => {
    return product?.is_digital === true || product?.is_digital === 'true' || product?.is_digital === 1;
  }, []);

  // Helper to get listing type display
  const getListingTypeDisplay = useCallback((product) => {
    const uploadType = product.upload_type || 'product';
    return uploadType === 'service' ? 'Service' : 'Product';
  }, []);

  // Fetch more products with pagination
  const fetchMoreProducts = useCallback(async (page = 1, append = false) => {
    try {
      setLoadingMore(true);
      const response = await api.get(
        `${API_CONFIG.STORE.STORE_V3}/${companyId}?page=${page}&limit=${PRODUCTS_PER_PAGE}`
      );

      if (response.data.success) {
        const newProducts = response.data.products || [];
        const totalProducts = response.data.totalProducts || newProducts.length;
        const totalPages = Math.ceil(totalProducts / PRODUCTS_PER_PAGE);
        
        if (append) {
          setAllProducts(prev => [...prev, ...newProducts]);
          setProducts(prev => [...prev, ...newProducts]);
        } else {
          setAllProducts(newProducts);
          setProducts(newProducts);
        }
        
        setPagination({
          currentPage: page,
          totalPages: totalPages,
          totalProducts: totalProducts,
          hasNext: page < totalPages,
          hasPrev: page > 1,
          limit: PRODUCTS_PER_PAGE
        });
        
        setAllProductsLoaded(page >= totalPages);
      }
    } catch (error) {
      console.error('Error fetching more products:', error);
      showToast('Failed to load more products', 'error');
    } finally {
      setLoadingMore(false);
    }
  }, [companyId]);

  // Load more products
  const loadMoreProducts = useCallback(() => {
    if (!loadingMore && pagination.hasNext) {
      const nextPage = pagination.currentPage + 1;
      fetchMoreProducts(nextPage, true);
    }
  }, [loadingMore, pagination, fetchMoreProducts]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (allProductsLoaded || loadingMore) return;

    const productsSection = document.getElementById('products-section');
    if (productsSection) {
      const { bottom } = productsSection.getBoundingClientRect();
      const { innerHeight } = window;
      
      // Load more when user is 200px from bottom of products section
      if (bottom - innerHeight < 200) {
        loadMoreProducts();
      }
    }
  }, [allProductsLoaded, loadingMore, loadMoreProducts]);

  // Add scroll event listener
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Data fetching
  useEffect(() => {
    const fetchStoreData = async () => {
      try {
        setIsLoading(true);
        const response = await api.get(`${API_CONFIG.STORE.STORE_V3}/${companyId}`);

        if (response.data.success) {
          setStore(response.data.store);
          const productsData = response.data.products || [];
          
          // Handle both paginated and non-paginated responses
          if (response.data.pagination) {
            setAllProducts(productsData);
            setProducts(productsData);
            setPagination(response.data.pagination);
            setAllProductsLoaded(!response.data.pagination.hasNext);
          } else {
            // Fallback to original behavior
            setAllProducts(productsData);
            setProducts(productsData);
            setPagination(prev => ({
              ...prev,
              totalProducts: productsData.length,
              totalPages: Math.ceil(productsData.length / PRODUCTS_PER_PAGE),
              hasNext: false
            }));
            setAllProductsLoaded(true);
          }

          // Extract categories with product counts
          const categoryMap = {};
          productsData.forEach(product => {
            if (product.category) {
              categoryMap[product.category] = (categoryMap[product.category] || 0) + 1;
            }
          });

          const uniqueCategories = Object.keys(categoryMap).map(category => ({
            // Strip mangled {uXXXXX} unicode-escape prefixes from category names.
            name: sanitizeString(category),
            count: categoryMap[category]
          }));

          setCategories(uniqueCategories);

          // Set price range based on actual products
          if (productsData.length > 0) {
            const prices = productsData.map(p => p.per_unit_cost || 0).filter(p => p > 0);
            if (prices.length > 0) {
              const minPrice = Math.min(...prices);
              const maxPrice = Math.max(...prices);
              setPriceRange([minPrice, maxPrice]);
            }
          }
        } else {
          showToast('Store not found', 'error');
        }
      } catch (error) {
        console.error('Error fetching store data:', error);
        showToast('Failed to load store', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    if (companyId) {
      fetchStoreData();
    }
  }, [companyId]);

  // Show Meo agent on store pages
  useEffect(() => {
    setShowMeo(true);
    return () => setShowMeo(false);
  }, [setShowMeo]);

  // Make store data and products available globally for Meo AI agent
  useEffect(() => {
    if (store) {
      window.__COMPANY_DATA__ = store;
    }
  }, [store]);

  useEffect(() => {
    if (allProducts.length > 0) {
      window.__STORE_PRODUCTS__ = allProducts;
    }
  }, [allProducts]);

  // Filtering and sorting
  useEffect(() => {
    let filtered = allProducts;

    // Category filter (compare sanitized names so mangled prefixes don't break filtering)
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => sanitizeString(product.category) === selectedCategory);
    }

    // Price range filter
    filtered = filtered.filter(product => {
      const price = product.per_unit_cost || 0;
      return price >= priceRange[0] && price <= priceRange[1];
    });

    // Sorting
    switch (sortBy) {
      case 'price-low':
        filtered = [...filtered].sort((a, b) => (a.per_unit_cost || 0) - (b.per_unit_cost || 0));
        break;
      case 'price-high':
        filtered = [...filtered].sort((a, b) => (b.per_unit_cost || 0) - (a.per_unit_cost || 0));
        break;
      case 'name':
        filtered = [...filtered].sort((a, b) => (a.product_name || '').localeCompare(b.product_name || ''));
        break;
      case 'popular':
        filtered = [...filtered].sort((a, b) => (b.total_sold || 0) - (a.total_sold || 0));
        break;
      case 'newest':
      default:
        filtered = [...filtered].sort((a, b) => new Date(b.date_uploaded || 0) - new Date(a.date_uploaded || 0));
        break;
    }

    setFilteredProducts(filtered);
    setCurrentPage(1);
  }, [allProducts, selectedCategory, sortBy, priceRange]);

  // Pagination calculation for current view
  const indexOfLastProduct = currentPage * PRODUCTS_PER_PAGE;
  const indexOfFirstProduct = indexOfLastProduct - PRODUCTS_PER_PAGE;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <div className="min-h-screen bg-gray-50 dark:bg-black text-gray-900 dark:text-white transition-colors duration-200">
      <Header cartItemCount={0} />
      <div className="max-w-7xl mx-auto px-4 py-4">
        {/* Store Header Skeleton */}
        <div className="animate-pulse mb-8">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-20 h-20 bg-gray-200 dark:bg-gray-800 rounded-full"></div>
            <div className="flex-1 space-y-3">
              <div className="h-6 bg-gray-200 dark:bg-gray-800 rounded w-1/3"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-1/2"></div>
            </div>
          </div>
        </div>

        {/* Products Grid Skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="space-y-3">
              <div className="aspect-square bg-gray-200 dark:bg-gray-800 rounded-2xl"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-3/4"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Error state component
  const ErrorState = () => (
    <div className="min-h-screen bg-white dark:bg-black flex items-center justify-center transition-colors duration-200">
      <div className="text-center max-w-md mx-auto px-4">
        <div className="w-full flex justify-center rounded-lg overflow-hidden mb-4">
          <div className="w-24 h-24 bg-gray-100 dark:bg-gray-900 rounded-full flex items-center justify-center">
             <ShoppingBag className="w-10 h-10 text-gray-400" />
          </div>
        </div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2" style={{ fontSize: '18px' }}>Store Not Available</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6" style={{ fontSize: '13px' }}>This store is currently unavailable or has been removed.</p>
        <div className="space-y-3">
          <Link
            to="/"
            className="block w-full bg-blue-600 text-white py-3 px-6 rounded-full hover:bg-blue-700 transition-colors font-bold"
            style={{ fontSize: '13px' }}
          >
            Continue Shopping
          </Link>
          <button
            onClick={() => window.location.reload()}
            className="block w-full border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 py-3 px-6 rounded-full hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors font-bold"
            style={{ fontSize: '13px' }}
          >
            Try Again
          </button>
        </div>
      </div>
    </div>
  );

  // Sidebar filters component
  const SidebarFilters = () => (
    <div className="hidden lg:block lg:w-64 flex-shrink-0">
      <div className="bg-white dark:bg-[#0a0a0a] rounded-2xl border border-gray-200 dark:border-gray-800 p-5 sticky top-24 space-y-6">
        {/* Categories */}
        <div>
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Categories</h3>
          <div className="space-y-1">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`w-full text-left px-3 py-2 rounded-lg transition-colors text-sm font-medium ${selectedCategory === 'all'
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900'
                }`}
              style={{ fontSize: '13px' }}
            >
              All Categories
              <span className="text-gray-400 ml-1 text-xs">({allProducts.length})</span>
            </button>
            {categories.map(category => (
              <button
                key={category.name}
                onClick={() => setSelectedCategory(category.name)}
                className={`w-full text-left px-3 py-2 rounded-lg transition-colors text-sm font-medium ${selectedCategory === category.name
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900'
                  }`}
                style={{ fontSize: '13px' }}
              >
                {category.name}
                <span className="text-gray-400 ml-1 text-xs">({category.count})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Price Range Filter */}
        <div className="border-t border-gray-200 dark:border-gray-800 pt-6">
          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Price Range</h3>
          <div className="space-y-3">
            <input
              type="range"
              min={min}
              max={max}
              step="0.1"
              value={priceRange[0]}
              onInput={(e) => {
                const v = Number(e.currentTarget.value);
                setPriceRange([v, priceRange[1]]);
              }}
              style={{
                background: `linear-gradient(to right, #1e5aff ${percent}%, #374151 ${percent}%)`,
              }}
              className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
              aria-label="Minimum price"
            />
            <div className="flex items-center justify-between text-xs font-medium text-gray-600 dark:text-gray-400" style={{ fontSize: '13px' }}>
              <span>{formatCurrency(priceRange[0])}</span>
              <span>{formatCurrency(priceRange[1])}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Toolbar component
  const Toolbar = () => (
    <div className="bg-white dark:bg-[#0a0a0a] rounded-2xl border border-gray-200 dark:border-gray-800 p-3 mb-4 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        {/* Filters */}
        <button
          onClick={() => setShowCategoriesModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-900 rounded-full hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors text-sm font-bold text-gray-700 dark:text-gray-300"
        >
          <Filter className="w-4 h-4" />
          <span className="font-medium text-[13px]">Filters</span>
        </button>

        {/* Sort By */}
        <div className="flex items-center space-x-2">
          <label className="text-xs text-gray-500 hidden sm:inline" style={{ fontSize: '13px' }}>
            Sort by:
          </label>
          <div className="relative"> 
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="appearance-none flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-900 rounded-full hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors text-sm font-bold text-gray-700 dark:text-gray-300 cursor-pointer"
                style={{ fontSize: '13px' }}
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="w-3 h-3 absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-xs text-gray-500 hidden sm:inline" style={{ fontSize: '13px' }}>
          {currentProducts.length} products
        </span>

        {/* View Mode Toggle */}
        <div className="flex items-center bg-gray-100 dark:bg-gray-900 rounded-lg p-0.5">
          <button
            onClick={() => setViewMode("grid")}
            className={`p-1.5 rounded-md transition-colors ${viewMode === "grid"
                ? "bg-white dark:bg-gray-800 text-black dark:text-white shadow-sm"
                : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              }`}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
              />
            </svg>
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={`p-1.5 rounded-md transition-colors ${viewMode === "list"
                ? "bg-white dark:bg-gray-800 text-black dark:text-white shadow-sm"
                : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              }`}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 10h16M4 14h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );

  // Product card component for grid view
  const ProductCard = ({ product }) => {
    const selectedVariation = selectedVariations[product.id];
    const availableStock = getAvailableStock(product, selectedVariation);
    const finalPrice = selectedVariation
      ? product.per_unit_cost + (selectedVariation.price_adjustment || 0)
      : product.per_unit_cost;

    const productImage = getProductImage(product);
    const isDigital = isDigitalProduct(product);
    const listingType = getListingTypeDisplay(product);

    return (
      <div className="bg-white dark:bg-[#0a0a0a] rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden hover:border-gray-300 dark:hover:border-gray-700 transition-all duration-200 group shadow-sm">
        <div className="aspect-[4/5] sm:aspect-square bg-gray-100 dark:bg-gray-900 relative overflow-hidden">
          {productImage && !isProductImageBroken(product.slug || product.id) ? (
            <Link to={`/product/${product.slug || product.id}`}>
              <img
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                src={productImage}
                alt={product.product_name}
                onError={() => markProductImageBroken(product.slug || product.id)}
              />
            </Link>
          ) : (
            <Link to={`/product/${product.slug || product.product_id}`}>
              <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-600">
                 <ShoppingBag className="w-8 h-8" />
              </div>
            </Link>
          )}

          {/* Image Count Badge for multiple images */}
          {product.images && product.images.length > 1 && (
            <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
              {product.images.length} photos
            </div>
          )}

          {/* Digital Product Badge */}
          {isDigital && (
            <div className="absolute top-2 left-2 bg-purple-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
              Digital
            </div>
          )}

          {/* Negotiable Badge */}
          {product?.is_negotiable && product?.starting_price > 0 && product?.ending_price > 0 && Number(product.starting_price) < Number(product.ending_price) && (
            <div className="absolute top-2 right-2 bg-black text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
              Negotiable
            </div>
          )}

          {/* Quick Actions */}
          <div className="absolute bottom-3 right-3 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <button
              onClick={() => addToCart(product, selectedVariation)}
              disabled={!isDigital && listingType !== 'Service' && availableStock <= 0}
              title={isDigital ? "Add digital product to cart" : (availableStock <= 0 ? "Out of stock" : "Add to cart")}
              className={`w-10 h-10 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-110 active:scale-95 ${
                !isDigital && listingType !== 'Service' && availableStock <= 0 
                  ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed' 
                  : 'bg-white dark:bg-black text-black dark:text-white'
              }`}
            >
              <ShoppingBag className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="p-3">
          <h3 className="font-bold text-sm text-black dark:text-white mb-1 line-clamp-1" style={{ fontSize: '13px' }}>
            <Link to={`/product/${product.slug || product.id}`} className="hover:text-blue-500 transition-colors">
              {product.product_name}
            </Link>
          </h3>

          <div className="flex items-center justify-between">
            <span className="font-bold text-blue-600 dark:text-blue-400 text-sm" style={{ fontSize: '13px' }}>
              {formatCurrency(finalPrice, product.currency)}
            </span>
            {/* Stock Status - Only if not digital/service */}
             {!isDigital && listingType !== 'Service' && (
                 <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${availableStock > 0 ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'}`}>
                     {availableStock > 0 ? 'In Stock' : 'Out'}
                 </span>
             )}
          </div>
        </div>
      </div>
    );
  };

  const HomeLink = () => {
    if (currentUser) {
      return (
        <Link to="/dashboard" className="hover:text-blue-600 transition-colors">
          Dashboard
        </Link>
      );
    } else {
      return (
        <Link to="https://paymeo.co/marketplace" className="hover:text-blue-600 transition-colors">
          Home
        </Link>
      );
    }
  };

  // Products grid/list component
  const ProductsDisplay = () => {
    if (currentProducts.length === 0 && !pagination.hasNext) {
      return (
        <div className="bg-white dark:bg-[#0a0a0a] rounded-2xl border border-gray-200 dark:border-gray-800 p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-4">
            <Search className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="font-bold text-lg text-black dark:text-white mb-2" style={{ fontSize: '16px' }}>No products found</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm" style={{ fontSize: '13px' }}>
            {selectedCategory !== 'all'
              ? `No products found in the "${selectedCategory}" category.`
              : 'This store has no products available at the moment.'
            }
          </p>
          {selectedCategory !== 'all' && (
            <button
              onClick={() => setSelectedCategory('all')}
              className="bg-black dark:bg-white text-white dark:text-black px-6 py-2 rounded-full font-bold text-sm hover:opacity-90 transition-opacity"
              style={{ fontSize: '13px' }}
            >
              View All Products
            </button>
          )}
        </div>
      );
    }

    return (
      <div id="products-section">
        <div className={`
          ${viewMode === 'grid'
            ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4'
            : 'space-y-4'
          }`}
        >
          {currentProducts.map((product) =>
            viewMode === 'grid'
              ? <ProductCard key={product.id} product={product} />
              : <div key={product.id} className="bg-white dark:bg-[#0a0a0a] rounded-xl border border-gray-200 dark:border-gray-800 p-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-24 h-24 bg-gray-100 dark:bg-gray-900 rounded-lg overflow-hidden flex-shrink-0">
                      {getProductImage(product) && !isProductImageBroken(product.slug || product.id) ? (
                        <img
                          className="w-full h-full object-cover"
                          src={getProductImage(product)}
                          alt={product.product_name}
                          onError={() => markProductImageBroken(product.slug || product.id)}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <ShoppingBag className="w-8 h-8" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-black dark:text-white mb-1" style={{ fontSize: '14px' }}>
                        {product.product_name}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 mb-3 line-clamp-2 text-xs" style={{ fontSize: '13px' }}>
                        {product.description || 'No description available'}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-blue-600 dark:text-blue-400" style={{ fontSize: '13px' }}>
                          {formatCurrency(product.per_unit_cost, product.currency)}
                        </span>
                        <button
                          onClick={() => addToCart(product)}
                          className="bg-black dark:bg-white text-white dark:text-black py-2 px-4 rounded-full font-bold text-xs hover:opacity-90 transition-opacity"
                          style={{ fontSize: '13px' }}
                        >
                          Add to Cart
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
          )}
        </div>

        {/* View Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center space-x-2 mt-12">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-900 text-black dark:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-xs"
              style={{ fontSize: '13px' }}
            >
              Previous
            </button>

            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const page = currentPage <= 3 ? i + 1 :
                  currentPage >= totalPages - 2 ? totalPages - 4 + i :
                    currentPage - 2 + i;
                if (page > totalPages) return null;
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 rounded-lg border transition-colors font-medium text-xs ${currentPage === page
                        ? 'bg-black dark:bg-white text-white dark:text-black border-transparent'
                        : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900'
                      }`}
                    style={{ fontSize: '13px' }}
                  >
                    {page}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-900 text-black dark:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-xs"
              style={{ fontSize: '13px' }}
            >
              Next
            </button>
          </div>
        )}

        {/* Load More Products Button */}
        {pagination.hasNext && (
          <div className="flex justify-center mt-8">
            <button
              onClick={loadMoreProducts}
              disabled={loadingMore}
              className="px-8 py-3 bg-gray-100 dark:bg-gray-900 text-black dark:text-white rounded-full font-bold text-sm hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
              style={{ fontSize: '14px' }}
            >
              {loadingMore ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                  <span>Loading...</span>
                </div>
              ) : (
                'Load More Products'
              )}
            </button>
          </div>
        )}

        {/* All products loaded message */}
        {allProductsLoaded && allProducts.length > 0 && (
          <div className="text-center mt-8">
            <p className="text-gray-500 text-xs">
              All {pagination.totalProducts} products loaded
            </p>
          </div>
        )}
      </div>
    );
  };

  // Mobile categories modal component
  const CategoriesModal = () => (
    showCategoriesModal && (
      <div className="fixed inset-0 z-50 lg:hidden">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
          onClick={() => setShowCategoriesModal(false)}
        />

        {/* Modal Content */}
        <div className="absolute inset-y-0 right-0 w-[80%] max-w-sm bg-white dark:bg-black shadow-2xl transform transition-transform duration-300 ease-in-out flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-800">
              <h2 className="font-bold text-lg text-black dark:text-white">Filters</h2>
              <button
                onClick={() => setShowCategoriesModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-900 rounded-full transition-colors"
              >
                <ChevronDown className="w-6 h-6 rotate-90 text-black dark:text-white" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-5 space-y-6">
              {/* Categories */}
              <div>
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Categories</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => handleCategorySelect('all')}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors text-sm font-medium ${selectedCategory === 'all'
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900'
                      }`}
                    style={{ fontSize: '13px' }}
                  >
                    All Categories
                    <span className="text-gray-400 ml-1 text-xs">({allProducts.length})</span>
                  </button>
                  {categories.map(category => (
                    <button
                      key={category.name}
                      onClick={() => handleCategorySelect(category.name)}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-colors text-sm font-medium ${selectedCategory === category.name
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900'
                        }`}
                      style={{ fontSize: '13px' }}
                    >
                      {category.name}
                      <span className="text-gray-400 ml-1 text-xs">({category.count})</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Range Filter */}
              <div className="border-t border-gray-200 dark:border-gray-800 pt-6">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Price Range</h3>
                <div className="space-y-3">
                  <input
                    type="range"
                    min={min}
                    max={max}
                    step="0.1"
                    value={priceRange[0]}
                    onInput={(e) => {
                      const v = Number(e.currentTarget.value);
                      setPriceRange([v, priceRange[1]]);
                    }}
                    style={{
                      background: `linear-gradient(to right, #1e5aff ${percent}%, #374151 ${percent}%)`,
                    }}
                    className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                    aria-label="Minimum price"
                  />
                  <div className="flex items-center justify-between text-xs font-medium text-gray-600 dark:text-gray-400">
                    <span>{formatCurrency(priceRange[0])}</span>
                    <span>{formatCurrency(priceRange[1])}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-5 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-black">
              <button
                onClick={handlePriceRangeApply}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-full hover:bg-blue-700 transition-colors font-bold text-sm"
                style={{ fontSize: '13px' }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      
    )
  );

  if (isLoading) return <LoadingSkeleton />;
  if (!store) return <ErrorState />;

  return (
    <div className="min-h-screen bg-white dark:bg-black text-gray-900 dark:text-white font-sans transition-colors duration-200">
      <Header
        setShowCartModal={setShowCartModal}
        setShowContactModal={setShowContactModal}
        cartItemCount={cartItemCount}
        setShowShareStoreModal={setShowShareStoreModal}
      />

      {/* Store Header */}
      <div className="bg-white dark:bg-[#0a0a0a] border-b border-gray-200 dark:border-gray-800 pb-6">
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Breadcrumb - RESTORED & STYLED */}
          <nav
            className="flex items-center space-x-2 text-gray-500 dark:text-gray-400 mb-6"
            style={{ fontSize: "13px" }}
          >
            <HomeLink />
            <span>/</span>
            <span className="font-medium">Stores</span>
            <span>/</span>
            <span className="font-medium truncate text-black dark:text-white">
              {store.company_name}
            </span>
          </nav>

          {/* Banner Placeholder - Added back for consistency with design system */}
          <div className="h-32 sm:h-48 bg-gradient-to-r from-blue-600 to-purple-600 w-full rounded-2xl mb-12 relative">
             {/* Overlapping Avatar */}
             <div className="absolute -bottom-10 left-6 sm:left-10">
                 <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full border-4 border-white dark:border-black overflow-hidden bg-white dark:bg-gray-900 shadow-lg">
                    {store.company_logo && !storeLogoFailed ? (
                    <img
                        src={store.company_logo}
                        alt={store.company_name}
                        className="w-full h-full object-cover"
                        onError={handleStoreLogoError}
                    />
                    ) : (
                    <div className="w-full h-full flex items-center justify-center bg-blue-600 text-white text-2xl font-bold">
                        {store.company_name?.charAt(0) || "S"}
                    </div>
                    )}
                 </div>
             </div>
          </div>

          {/* Store Info Wrapper */}
          <div className="flex flex-col sm:flex-row items-start justify-between px-2">
            {/* Left side - Store Info */}
            <div className="flex flex-col w-full">
              <div className="flex-1">
                <h1
                  className="text-2xl font-bold text-black dark:text-white mb-2 flex items-center gap-2"
                  style={{ fontSize: "24px" }}
                >
                  {store.company_name}
                  <Check className="w-5 h-5 text-blue-500" />
                </h1>
                <p
                  className="text-gray-600 dark:text-gray-400 mb-4 max-w-2xl text-sm leading-relaxed"
                >
                  {store.company_description}
                </p>

                {/* Store Stats */}
                <div
                  className="flex flex-wrap items-center gap-x-6 text-gray-500 dark:text-gray-400 font-medium mb-6"
                  style={{ fontSize: "13px" }}
                >
                  <div className="flex items-center space-x-1">
                    <Star className="w-4 h-4 text-yellow-500 fill-current" />
                    <span>4.8</span>
                    <span className="text-gray-400">•</span>
                    <span>100+ reviews</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <span>🛒 {pagination.totalProducts || allProducts.length} products</span>
                    <span className="text-gray-400">•</span>
                    <span>🚚 Free shipping</span>
                  </div>
                </div>

                {/* Mobile Actions */}
                <div className="flex flex-wrap gap-3 xl:gap-4 sm:hidden">
                  <button
                    onClick={() => setShowContactModal(true)}
                    className="px-6 py-2.5 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors font-bold text-sm"
                  >
                    Contact
                  </button>
                  <button
                    onClick={() => setShowShareStoreModal(true)}
                    className="px-6 py-2.5 bg-black dark:bg-white text-white dark:text-black rounded-full hover:opacity-90 transition-opacity font-bold text-sm"
                  >
                    Share
                  </button>
                </div>
              </div>
            </div>

            {/* Right side - Actions (Desktop only) */}
            <div className="hidden sm:flex items-center space-x-3 mt-1">
              <button
                onClick={() => setShowContactModal(true)}
                className="px-6 py-2.5 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors font-bold text-sm"
              >
                Contact
              </button>
              <button
                onClick={() => setShowShareStoreModal(true)}
                className="px-6 py-2.5 bg-black dark:bg-white text-white dark:text-black rounded-full hover:opacity-90 transition-opacity font-bold text-sm"
              >
                Share
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          <SidebarFilters />

          {/* Main Content Area */}
          <div className="flex-1">
            <Toolbar />
            <ProductsDisplay />
          </div>
        </div>
      </div>

      <CategoriesModal />
      <StoreFrontFooter />

      {/* Modals */}
      <ContactStoreModal
        show={showContactModal}
        onHide={() => setShowContactModal(false)}
        store={store}
      />
      <CartModal
        show={showCartModal}
        onHide={() => setShowCartModal(false)}
        cart={cart}
        updateQuantity={updateQuantity}
        removeFromCart={removeFromCart}
        clearCart={clearCartLocal}
        cartTotal={cartTotal}
        store={store}
        companyId={companyId}
      />
      <ShareStoreModal
        show={showShareStoreModal}
        onClose={() => setShowShareStoreModal(false)}
        link={shareLink}
        product={store}
      />
    </div>
  );
};

export default StorePage;